import { useEffect, useMemo, useState } from "react";
import { hkInvAPI, fmtDT } from "../hkInvApi";
import { toast } from "sonner";
import { Plus, Activity, DoorClosed, Layers, Package, TrendingUp } from "lucide-react";
import { cls, Fld, Modal, Footer } from "../../kitchen/KitchenPantry";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { NumberInput } from "@/components/ui/number-input";

export default function HKUsageLog() {
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState(null);
  const [trendDays, setTrendDays] = useState(14);
  const [logging, setLogging] = useState(false);

  const load = () => {
    hkInvAPI.usage({ limit: 300 }).then(setRows).catch(()=>{});
    hkInvAPI.usageSummary(14).then(setSummary).catch(()=>{});
    hkInvAPI.usageTrend(trendDays).then(setTrend).catch(()=>{});
  };
  useEffect(() => { load(); hkInvAPI.items().then(setItems).catch(()=>{}); }, []);  // eslint-disable-line
  useEffect(() => { hkInvAPI.usageTrend(trendDays).then(setTrend).catch(()=>{}); }, [trendDays]);

  const loc = (u) => u.location_type === "room" ? (u.room_number ? `Room ${u.room_number}` : "Room")
                    : u.location_type === "floor" ? (u.floor ? `Floor ${u.floor}` : "Floor")
                    : "General";

  const chartData = useMemo(() => (trend?.series || []).map(p => ({
    label: p.date.slice(5),               // MM-DD
    qty: Number(p.qty || 0),
    events: p.events,
  })), [trend]);
  const totalUsed = useMemo(() => chartData.reduce((s, p) => s + p.qty, 0), [chartData]);

  return (
    <div className="space-y-4" data-testid="hkinv-usage">
      {/* Daily consumption trend */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="hkinv-usage-trend">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-hocp-navy"/>
          <h3 className="text-[13px] font-bold uppercase tracking-wider text-slate-600">Daily Consumption Trend</h3>
          <span className="text-[12px] text-slate-400">· {totalUsed} units over {trendDays}d</span>
          <div className="ml-auto flex gap-1">
            {[7, 14, 30].map(d => (
              <button key={d} onClick={()=>setTrendDays(d)} data-testid={`hkinv-trend-${d}`}
                      className={`h-7 px-2.5 rounded-md text-[11.5px] font-medium border ${
                        trendDays===d ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint"
                                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>
        {totalUsed === 0 ? (
          <div className="h-[180px] flex items-center justify-center text-[12.5px] text-slate-400">
            No consumption logged in this window yet.
          </div>
        ) : (
          <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer>
              <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="hkinvUsage" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1e3a5f" stopOpacity={0.28}/>
                    <stop offset="100%" stopColor="#1e3a5f" stopOpacity={0.02}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false}/>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval="preserveStartEnd"/>
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} width={40}/>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10, border: "1px solid #e2e8f0" }}
                         formatter={(v, n) => [v, n === "qty" ? "Units used" : n]}
                         labelFormatter={(l) => `Date ${l}`}/>
                <Area type="monotone" dataKey="qty" stroke="#1e3a5f" strokeWidth={2} fill="url(#hkinvUsage)" dot={{ r: 2 }} activeDot={{ r: 4 }}/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Usage-over-time summary */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="hkinv-usage-summary">
          <SummaryCard icon={Package} title={`Top items · last ${summary.days}d`}
            rows={summary.by_item.slice(0,6).map(x => [x.item_name, x.qty])} empty="No usage yet"/>
          <SummaryCard icon={DoorClosed} title={`By room · last ${summary.days}d`}
            rows={summary.by_room.slice(0,6).map(x => [`Room ${x.room_number}`, x.qty])} empty="No room usage yet"/>
          <SummaryCard icon={Layers} title={`By floor · last ${summary.days}d`}
            rows={summary.by_floor.slice(0,6).map(x => [`Floor ${x.floor}`, x.qty])} empty="No floor usage yet"/>
        </div>
      )}

      <div className="flex items-center gap-3">
        <h3 className="text-[14px] font-bold uppercase tracking-wider text-slate-500">Consumption Log</h3>
        <button onClick={()=>setLogging(true)} data-testid="hkinv-usage-log-btn"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-semibold inline-flex items-center gap-2 hover:brightness-95">
          <Plus className="w-4 h-4"/> Log Usage
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-12 text-center">
            <Activity className="w-12 h-12 mx-auto text-slate-300 mb-2"/>
            <div className="text-[13px] text-slate-500">No usage logged yet.</div>
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Item</th>
                <th className="text-right px-4 py-3">Qty</th>
                <th className="text-left px-4 py-3">Location</th>
                <th className="text-left px-4 py-3">Logged By</th>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(u => (
                <tr key={u.id} data-testid={`hkinv-usage-row-${u.id}`} className="hover:bg-slate-50/50">
                  <td className="px-4 py-3 font-medium text-hocp-navy">{u.item_name}</td>
                  <td className="px-4 py-3 text-right mono font-semibold tabular-nums">{u.qty} {u.unit}</td>
                  <td className="px-4 py-3 text-slate-600">{loc(u)}</td>
                  <td className="px-4 py-3 text-[12px] text-slate-500">{u.actor_email}</td>
                  <td className="px-4 py-3 text-[12px] text-slate-500 mono">{fmtDT(u.at)}</td>
                  <td className="px-4 py-3 text-[12px] text-slate-500 truncate max-w-xs">{u.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {logging && <UsageDialog items={items} onClose={()=>setLogging(false)} onDone={()=>{ setLogging(false); load(); }}/>}
    </div>
  );
}

function SummaryCard({ icon: Icon, title, rows, empty }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-3">
        <Icon className="w-3.5 h-3.5"/>{title}
      </div>
      {rows.length === 0 ? (
        <div className="text-[12.5px] text-slate-400 py-2">{empty}</div>
      ) : (
        <ul className="space-y-1.5">
          {rows.map(([k, v], i) => (
            <li key={i} className="flex items-center justify-between text-[13px]">
              <span className="text-slate-600 truncate">{k}</span>
              <span className="mono font-semibold tabular-nums text-hocp-navy">{v}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function UsageDialog({ items, onClose, onDone }) {
  const [f, setF] = useState({ item_id: "", item_name: "", unit: "pcs", qty: "",
                               location_type: "room", room_number: "", floor: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const pick = (id) => { const p = items.find(x => x.id === id); if (p) setF(s => ({ ...s, item_id: p.id, item_name: p.item_name, unit: p.unit })); };
  const submit = async () => {
    if (!f.item_id || !(Number(f.qty) > 0)) { toast.error("Pick an item and a quantity greater than 0"); return; }
    if (f.location_type === "room" && !f.room_number) { toast.error("Room number is required"); return; }
    if (f.location_type === "floor" && !f.floor) { toast.error("Floor is required"); return; }
    setSaving(true);
    try {
      await hkInvAPI.createUsage({
        item_id: f.item_id, item_name: f.item_name, qty: f.qty, unit: f.unit,
        location_type: f.location_type,
        room_number: f.location_type === "room" ? f.room_number : null,
        floor: f.location_type === "floor" ? f.floor : null,
        notes: f.notes || null,
      });
      toast.success("Usage logged"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Log Consumption" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Item" full>
          <select value={f.item_id} onChange={e=>pick(e.target.value)} data-testid="hkinv-usage-item" className={cls}>
            <option value="">Select item…</option>
            {items.map(x => <option key={x.id} value={x.id}>{x.item_name} — {x.current_stock} {x.unit}</option>)}
          </select>
        </Fld>
        <Fld label="Quantity used"><NumberInput step="0.001" value={f.qty} onChange={e=>setF({...f, qty: e.target.value})} placeholder="e.g. 4" data-testid="hkinv-usage-qty" className={cls}/></Fld>
        <Fld label="Location type">
          <select value={f.location_type} onChange={e=>setF({...f, location_type: e.target.value})} data-testid="hkinv-usage-loctype" className={cls}>
            <option value="room">Per room</option>
            <option value="floor">Per floor</option>
            <option value="general">General</option>
          </select>
        </Fld>
        {f.location_type === "room" && (
          <Fld label="Room number" full><input value={f.room_number} onChange={e=>setF({...f, room_number: e.target.value})} placeholder="e.g. 305" data-testid="hkinv-usage-room" className={cls}/></Fld>
        )}
        {f.location_type === "floor" && (
          <Fld label="Floor" full><input value={f.floor} onChange={e=>setF({...f, floor: e.target.value})} placeholder="e.g. 3" data-testid="hkinv-usage-floor" className={cls}/></Fld>
        )}
        <Fld label="Notes (optional)" full>
          <input value={f.notes} onChange={e=>setF({...f, notes: e.target.value})} placeholder="e.g. VIP turndown" data-testid="hkinv-usage-notes" className={cls}/>
        </Fld>
      </div>
      <Footer onClose={onClose} onSubmit={submit} saving={saving} testid="hkinv-usage-save"/>
    </Modal>
  );
}
